/**
 * `numeric` — type a number, into one entry field or several, marked against an inclusive
 * interval.
 *
 * The comparison is two `<=` against literals the author wrote, and that is the entire point of
 * the type's design. See `NumericSchemeDoc` for why an `equals` + `tolerance` scheme loses real
 * answers to float arithmetic; the short version is that this function must do no arithmetic on
 * the learner's number, and it does none.
 *
 * What the asset drew — one box, four boxes, a slider that reports a string — is its own
 * business. The reported answer is the text of each field, in the order the fields were drawn.
 */

import { isNumericResponse } from '@cet/asset-contract';
import type { ItemResponse, ItemVerdict, Locale, NumericSchemeDoc, VerdictPart } from '@cet/asset-contract';

import { ScoringError } from '../errors.js';
import { stripSpacing } from '../normalize.js';
import { graded, unanswered } from '../verdicts.js';

/**
 * What counts as a number typed into a field.
 *
 * Strict, and deliberately narrower than `Number()`: that accepts `"0x1f"`, `"Infinity"`, `" "`
 * as zero and `""` as zero, none of which a learner meant. An optional sign, digits, an optional
 * decimal part, and a leading `.` for the `.5` an author will not have thought about.
 *
 * **No exponent notation** in Phase 1, and **no decimal comma**. Hebrew writes a decimal point,
 * so the comma is not a gap for the locales that exist today; Arabic writes `٫` with
 * Arabic-Indic digits, and that arrives with the `ar` profile in Phase 2, as a change to this
 * regex and its fixtures rather than something to guess at now.
 */
const NUMBER = /^[+-]?(\d+(\.\d+)?|\.\d+)$/;

/** Blank, unparseable, or a value. Kept apart because blank is not wrong (D32). */
type Field = { readonly state: 'blank' } | { readonly state: 'bad' } | { readonly state: 'value'; readonly value: number };

function readField(raw: string | undefined): Field {
  if (raw === undefined) return { state: 'blank' };

  const cleaned = stripSpacing(raw);
  // Checked after stripping, so a field holding only a no-break space pasted from a document is
  // still blank rather than a wrong answer.
  if (cleaned === '') return { state: 'blank' };

  if (!NUMBER.test(cleaned)) return { state: 'bad' };

  const value = Number(cleaned);
  // Unreachable for anything the regex admits — a long run of digits overflows to Infinity, which
  // this catches rather than comparing it against a bound.
  return Number.isFinite(value) ? { state: 'value', value } : { state: 'bad' };
}

export function scoreNumeric(
  itemId: string,
  scheme: NumericSchemeDoc,
  response: ItemResponse | null | undefined,
  locale: Locale,
): ItemVerdict {
  if (response === null || response === undefined) {
    return unanswered(itemId, scheme.points);
  }

  if (!isNumericResponse(response)) {
    throw new ScoringError(
      'invalid-response',
      `Item "${itemId}" is a numeric item, so its response must be { values: string[] } — one ` +
        `entry per field, as strings. Received keys: ${Object.keys(response).join(', ') || '(none)'}.`,
      `responses.${itemId}`,
    );
  }

  const fields = scheme.fields ?? 1;
  const read = response.values.map(readField);

  // Every field blank is a blank item, not a wrong one (D32). An asset that sends `values: []`,
  // or four empty strings, means the same thing and must not be told it answered incorrectly.
  if (read.every((field) => field.state === 'blank')) {
    return unanswered(itemId, scheme.points);
  }

  /**
   * Values already accepted, so `distinct` can reject a repeat.
   *
   * Compared as parsed numbers rather than as the strings typed, so `2.20` is a repeat of `2.2` —
   * they are the same answer, and a learner who wrote both has given one number twice. Only
   * *accepted* values are recorded: a repeat of a value that was itself out of range is already
   * wrong for that reason, and counting it twice would report a cause that is not the real one.
   */
  const seen: number[] = [];
  const parts: VerdictPart[] = [];
  let valid = 0;

  for (let index = 0; index < fields; index += 1) {
    const field = read[index] ?? { state: 'blank' as const };

    let right = false;
    if (field.state === 'value') {
      // The whole comparison. Two `<=` against the author's literals, no arithmetic — which is
      // what makes `4.4` inside `[4.30, 4.40]` come out true where `|4.4 - 4.35| <= 0.05` does not.
      const inRange = field.value >= scheme.min && field.value <= scheme.max;
      const repeat = scheme.distinct === true && seen.includes(field.value);
      right = inRange && !repeat;
      if (right) seen.push(field.value);
    }

    if (right) valid += 1;

    /**
     * Every declared field is reported, so `parts.length` is the item's field count and an asset
     * can use it as a row count — which it can here and cannot for `ordering`, because the count
     * is fixed by the content rather than by what the learner did.
     *
     * The id is the field's position as a string. `matching` reports a left-hand id and `ordering`
     * a choice id, but a numeric item's content declares no ids: the asset drew box 0, box 1, and
     * indexes them by position, so the position *is* its own vocabulary and needs no mapping.
     *
     * Note the open question this inherits: a field the learner left blank among filled ones is
     * reported `correct: false`, which reads as "this box is wrong" for a box nobody has started.
     * That is `matching`'s answer rather than a fourth one — see GAPS.md.
     */
    parts.push({ id: String(index), correct: right });
  }

  // More values than fields means the answer is not the one the item asked for, even if every
  // field that exists happens to be right — the same judgement `ordering` makes about a five-item
  // answer to a four-item question.
  const whollyCorrect = valid === fields && response.values.length === fields;

  const awarded =
    (scheme.credit ?? 'all') === 'partial'
      ? // Integer arithmetic: scores never accumulate as floats, so this floors.
        Math.floor((scheme.points * valid) / fields)
      : whollyCorrect
        ? scheme.points
        : 0;

  return graded(
    itemId,
    { awarded: whollyCorrect ? scheme.points : awarded, max: scheme.points },
    scheme.feedback,
    whollyCorrect,
    locale,
    parts,
  );
}
